// The platform's interface text, in the language the reader chose.
//
// A message is keyed by its English wording — `t("Save survey")` — so the code
// reads as it always did, English needs no catalogue of its own, and a label
// that sits in a plain array is translated wherever it is rendered. A wording
// the catalogue does not have comes back in English, never blank.
//
// Placeholders and plurals use ICU syntax:
//    t("Hello {name}", { name })
//    t("{count, plural, one {# survey} other {# surveys}}", { count })
// A value may be a React element; the result is then a fragment, not a string.
// A value that is a function wraps a tag, so a translation can move the
// emphasis with the words: t("<b>Unlimited</b> workspaces", { b: (text) => <b>{text}</b> })
//
// The catalogue is installed before the app's own modules run (src/index.js),
// and changing the language reloads the page, so `t` works anywhere: in a
// component, a Yup schema, or a constant at the top of a module.
import React from "react";
import IntlMessageFormat from "intl-messageformat";

// Arabic is written with Western digits here, as the numbers in tables, charts
// and exports already are; `ar` alone would print plural counts as ٣.
const FORMAT_LOCALE = { ar: "ar-u-nu-latn" };

// Joins a message's context to its wording, the way gettext's msgctxt does, for
// the rare English word that two screens need translated differently.
const CONTEXT_SEPARATOR = "|";

let current = { locale: "en", messages: {} };
const formatters = new Map();

export function installTranslations(locale, messages) {
   current = { locale, messages: messages || {} };
   formatters.clear();
}

export const getLocale = () => current.locale;

/** The locale to hand Intl and toLocale*String, with Arabic's Western digits. */
export const getIntlLocale = () => FORMAT_LOCALE[current.locale] || current.locale;

const formatterFor = (message) => {
   let formatter = formatters.get(message);
   if (!formatter) {
      formatter = new IntlMessageFormat(message, getIntlLocale());
      formatters.set(message, formatter);
   }
   return formatter;
};

const TAG = /(<\/?[A-Za-z][A-Za-z0-9]*>)/;

// Wraps what sits between <name> and </name> in the function given as `name`;
// any other text that looks like a tag is left as written.
const applyTags = (nodes, tags) => {
   const root = { children: [] };
   const stack = [root];
   nodes.forEach((node) => {
      const pieces = typeof node === "string" ? node.split(TAG) : [node];
      pieces.forEach((piece) => {
         if (piece === "") return;
         const tag = typeof piece === "string" && piece.match(/^<(\/?)([A-Za-z][A-Za-z0-9]*)>$/);
         if (tag && tags[tag[2]]) {
            if (!tag[1]) return stack.push({ name: tag[2], children: [] });
            if (stack.length > 1 && stack[stack.length - 1].name === tag[2]) {
               const { name, children } = stack.pop();
               stack[stack.length - 1].children.push(tags[name](asNode(children)));
               return;
            }
         }
         stack[stack.length - 1].children.push(piece);
      });
   });
   while (stack.length > 1) {
      const open = stack.pop();
      stack[stack.length - 1].children.push(`<${open.name}>`, ...open.children);
   }
   return root.children;
};

const asNode = (children) =>
   children.length === 1 ? children[0] : React.createElement(React.Fragment, null, ...children);

const format = (message, values) => {
   if (!values) return message;
   try {
      const tags = {};
      const args = {};
      Object.keys(values).forEach((key) => {
         (typeof values[key] === "function" ? tags : args)[key] = values[key];
      });
      let nodes = formatterFor(message)
         .formatToParts(args)
         .map((part) => part.value);
      if (Object.keys(tags).length) nodes = applyTags(nodes, tags);
      if (nodes.every((node) => typeof node === "string")) return nodes.join("");
      return React.createElement(React.Fragment, null, ...nodes);
   } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      return message;
   }
};

export function t(message, values) {
   return format(current.messages[message] || message, values);
}

// `tc("survey status", "Open")` — the same wording, kept apart from the "Open"
// on a button. Falls back to the English wording without its context.
export function tc(context, message, values) {
   return format(current.messages[context + CONTEXT_SEPARATOR + message] || message, values);
}
